import { z } from "zod";

/**
 * L'ACCES PAR FICHE : equipe, prive, ancre.
 *
 * Les rules cadrent par organisation et par porte (`docs/ACCES.md`). Ce module
 * ajoute, sur une LISTE FERMEE de familles, un champ `acces` qui reserve une
 * fiche a ses participants, ou la fait heriter des droits d'une autre fiche.
 * Le raisonnement complet : `docs/socle/acces-par-fiche.md`.
 *
 * Trois formes :
 *   - `equipe` : la porte decide seule, c'est ce qui existait ;
 *   - `prive`  : seuls les `participants` lisent et ecrivent ; le
 *                `proprietaire` en fait partie et c'est lui qui change la
 *                liste (un participant peut se retirer lui-meme) ;
 *   - `ancre`  : la fiche herite des droits de la fiche `ancre` (le chatter
 *                d'un contact se lit par qui lit ce contact). Un seul niveau :
 *                pas d'ancre sur une fiche elle-meme ancree.
 *
 * Les participants et le proprietaire sont des `personId` (`personne.ts`),
 * jamais des uid : une personne invitee avant sa premiere connexion n'a pas
 * d'uid, et les rules lisent deja le `personId` de l'appelant sur sa fiche
 * membre, sans lecture de plus.
 *
 * LA LISTE DES FAMILLES EST ECRITE TROIS FOIS, et c'est assume : ici, dans
 * `firebase/firestore.rules` (`estFamilleAccesParFiche`) et dans
 * `functions/src/ia/acces-par-fiche.ts`. Les rules n'importent rien, et les
 * Functions ne dependent pas de ce package. Le test
 * `packages/sdk/tests/acces-par-fiche.test.mjs` compare les trois.
 */

/** Les familles dont les fiches portent un `acces`. Fermee : en ajouter une, c'est un chantier de socle. */
export const FAMILLES_ACCES_PAR_FICHE = ["contents", "conversations", "messages"] as const;

/** Les familles HERITIERES : la fiche porte `conversationId` et un reflet de l'acces de sa conversation. */
export const FAMILLES_HERITIERES = ["messages"] as const;

export function estFamilleAccesParFiche(famille: string): boolean {
  return (FAMILLES_ACCES_PAR_FICHE as readonly string[]).includes(famille);
}

export function estFamilleHeritiere(famille: string): boolean {
  return (FAMILLES_HERITIERES as readonly string[]).includes(famille);
}

/** Les trois visibilites, dans les rules comme ici. */
export const VISIBILITES = ["equipe", "prive", "ancre"] as const;
export const visibiliteSchema = z.enum(VISIBILITES);
export type Visibilite = z.infer<typeof visibiliteSchema>;

/** Le plafond de participants d'une fiche privee, recopie dans les rules. */
export const ACCES_PARTICIPANTS_MAX = 100;

/** La fiche dont une fiche ancree herite les droits. */
export const ancreSchema = z.object({
  family: z.string().min(1),
  recordId: z.string().min(1),
});
export type Ancre = z.infer<typeof ancreSchema>;

export const accesEquipeSchema = z.object({ visibilite: z.literal("equipe") }).strict();
export const accesPriveSchema = z
  .object({
    visibilite: z.literal("prive"),
    proprietaire: z.string().min(1),
    participants: z.array(z.string().min(1)).min(1).max(ACCES_PARTICIPANTS_MAX),
  })
  .strict();
export const accesAncreSchema = z.object({ visibilite: z.literal("ancre"), ancre: ancreSchema }).strict();

/** L'acces d'une fiche PORTEUSE (`contents`, `conversations`), tel qu'il est stocke. */
export const accesSchema = z.discriminatedUnion("visibilite", [
  accesEquipeSchema,
  accesPriveSchema,
  accesAncreSchema,
]);
export type Acces = z.infer<typeof accesSchema>;

/** Le REFLET porte par une fiche heritiere (`messages`) : la visibilite de sa conversation, rien d'autre. */
export const accesRefletSchema = z.object({ visibilite: visibiliteSchema }).strict();
export type AccesReflet = z.infer<typeof accesRefletSchema>;

/** L'acces equipe, la valeur d'une fiche d'avant le chantier. */
export const ACCES_EQUIPE: Acces = { visibilite: "equipe" };

/**
 * Le champ `acces` d'une famille porteuse. OPTIONNEL a la lecture : une fiche
 * ecrite avant le chantier ne le porte pas et se lit comme equipe
 * (`accesDe`). Le rattrapage : `scripts/backfill-acces.mjs`.
 */
export const accesField = { acces: accesSchema.optional() } as const;

/** Le champ `acces` d'une famille heritiere. */
export const accesRefletField = { acces: accesRefletSchema.optional() } as const;

/**
 * Ce que l'APPELANT fournit a la creation d'une fiche porteuse. Le
 * proprietaire n'y est pas : c'est le SDK qui le pose (le `personId` de
 * l'appelant) et qui l'ajoute aux participants.
 */
export const accesInputSchema = z.discriminatedUnion("visibilite", [
  accesEquipeSchema,
  z
    .object({
      visibilite: z.literal("prive"),
      participants: z.array(z.string().min(1)).max(ACCES_PARTICIPANTS_MAX).optional(),
    })
    .strict(),
  accesAncreSchema,
]);
export type AccesInput = z.infer<typeof accesInputSchema>;

/** Le champ `acces` d'une entree de creation. Absent : equipe. */
export const accesInputField = { acces: accesInputSchema.optional() } as const;

/** L'acces d'une fiche lue : equipe quand elle n'en porte pas encore. */
export function accesDe(fiche: { acces?: Acces | undefined }): Acces {
  return fiche.acces ?? ACCES_EQUIPE;
}

/** La personne est-elle participante d'un acces prive ? Faux sans `personId`. */
export function estParticipant(acces: Acces | undefined, personId: string | null | undefined): boolean {
  if (acces === undefined || acces.visibilite !== "prive") return false;
  return typeof personId === "string" && personId.length > 0 && acces.participants.includes(personId);
}

/** La personne est-elle proprietaire d'un acces prive ? Faux sans `personId`. */
export function estProprietaire(acces: Acces | undefined, personId: string | null | undefined): boolean {
  if (acces === undefined || acces.visibilite !== "prive") return false;
  return typeof personId === "string" && personId.length > 0 && acces.proprietaire === personId;
}

/**
 * L'ACCES A STOCKER d'une fiche porteuse qui nait, a partir de l'entree de
 * l'appelant et du `personId` de l'appelant. Meme regle que les rules : un
 * acces prive exige un `personId`, le proprietaire est dans la liste, sans
 * doublon. Jete une erreur nommee quand l'appelant n'a pas de fiche membre.
 */
export function accesACreer(input: AccesInput | undefined, personId: string | null): Acces {
  if (input === undefined || input.visibilite === "equipe") return ACCES_EQUIPE;
  if (input.visibilite === "ancre") return { visibilite: "ancre", ancre: input.ancre };
  if (personId === null || personId.length === 0) {
    throw new SansPersonne();
  }
  const participants = [...new Set([personId, ...(input.participants ?? [])])];
  if (participants.length > ACCES_PARTICIPANTS_MAX) {
    throw new Error(
      `acces : ${participants.length} participants, plafond ${ACCES_PARTICIPANTS_MAX} (ACCES_PARTICIPANTS_MAX).`,
    );
  }
  return { visibilite: "prive", proprietaire: personId, participants };
}

/**
 * L'appelant n'a pas de `personId` : pas de fiche membre (compte provisionne
 * par claims seuls), ou une fiche d'avant l'annuaire. Il garde tout ce qui est
 * equipe, et ne peut ni creer du prive ni en recevoir.
 */
export class SansPersonne extends Error {
  constructor() {
    super(
      "Acces prive impossible : ce compte n'a pas de fiche membre (ou pas de personId). " +
        "Un administrateur lui en cree une depuis Parametres > Utilisateurs.",
    );
    this.name = "SansPersonne";
  }
}
